import { fade } from "@material-ui/core/styles";
import PhoneCallButton from "./PhoneCallButton";
import CopyTextButton from "./CopyTextButton";
import { formatQuestionLists } from "./questionLists";
import Autocomplete from "@material-ui/lab/Autocomplete";
import "react-multi-carousel/lib/styles.css";
import "ol/ol.css";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
  makeStyles,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import CloseIcon from "@material-ui/icons/Close";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import FullscreenExitIcon from "@material-ui/icons/FullscreenExit";
import GetAppIcon from "@material-ui/icons/GetApp";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import moment from "moment";
import Map from "ol/Map";
import View from "ol/View";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import Overlay from "ol/Overlay";
import OverlayPositioning from "ol/OverlayPositioning";
import OSM from "ol/source/OSM";
import XYZ from "ol/source/XYZ";
import TileWMS from "ol/source/TileWMS";
import VectorSource from "ol/source/Vector";
import { toLonLat, fromLonLat } from "ol/proj";
import { Circle as CircleStyle, Fill, Stroke, Style } from "ol/style";
import React, { useEffect, useRef, useState } from "react"; // @ts-ignore // @ts-ignore
import { useHistory } from "react-router-dom";

import {
  Block,
  MapComment,
  PageContent,
  RootURL,
  SiteTeamMember,
  getPublicMapComments,
  submitMapComment,
  submitContactForm,
  isAuthenticatedPreview,
} from "../services/cms-api";
import TimelineCards from "./TimelineCards";
import EventCalendar from "./EventCalendar";


interface CmsBlockRendererProps {
  block: Block;
  teamMembers?: SiteTeamMember[];
  primaryColor?: string;
  allPages?: PageContent[];
  basePath?: string;
  projectLeader?: number | null;
  siteId?: number;
}

const LGL_ORTHOPHOTO_TILES = "https://owsproxy.lgl-bw.de/owsproxy/ows/WMTS_LGL-BW_ATKIS_DOP_20_C"
  + "?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=DOP_20_C&STYLE=default"
  + "&FORMAT=image/jpeg&TILEMATRIXSET=GoogleMapsCompatible&TILEMATRIX=GoogleMapsCompatible:{z}"
  + "&TILEROW={y}&TILECOL={x}";

const LGL_ORTHOPHOTO_WMS = "https://owsproxy.lgl-bw.de/owsproxy/ows/WMS_LGL-BW_ATKIS_DOP_20_C";
const MAX_ORTHOPHOTO_ZOOM = 19;

function PublicOrthophotoMap({ block }: { block: Block }) {
  const mobile = useMediaQuery("(max-width:600px)");
  const mapElement = useRef<HTMLDivElement | null>(null);
  const liveView = useRef<View | null>(null);
  const [maximized, setMaximized] = useState(false);
  const [imageryUnavailable, setImageryUnavailable] = useState(false);
  const [comments, setComments] = useState<MapComment[]>([]);
  const [draftPoint, setDraftPoint] = useState<[number, number] | null>(null);
  const [pointTitle, setPointTitle] = useState("");
  const [pointComment, setPointComment] = useState("");
  const [pointCategory, setPointCategory] = useState("");
  const [savingPoint, setSavingPoint] = useState(false);
  const [pointError, setPointError] = useState("");
  const [pointNotice, setPointNotice] = useState("");
  const [addingPoint, setAddingPoint] = useState(false);
  const addingPointRef = useRef(false);
  const settings = block.settings || {};
  const allowComments = settings.allow_comments !== false;
  const savePoint = async () => {
    if (!draftPoint || !pointTitle.trim() || savingPoint || !allowComments) return;
    setSavingPoint(true); setPointError("");
    try {
      const result = await submitMapComment(block.id, { title: pointTitle.trim(), comment: pointComment.trim(), category: pointCategory,
        geom: { type: "Point", coordinates: draftPoint } });
      setPointNotice(result.detail);
      setDraftPoint(null); setPointTitle(""); setPointComment(""); setPointCategory("");
      addingPointRef.current = false; setAddingPoint(false);
    } catch (error) {
      setPointError((error as any).response?.data?.detail || "Der Beitrag konnte nicht gesendet werden. Bitte erneut versuchen.");
    } finally { setSavingPoint(false); }
  };
  const coordinates = settings.center && settings.center.coordinates;
  const longitude = Number((coordinates && coordinates[0]) || settings.center_longitude || 9.2219);
  const latitude = Number((coordinates && coordinates[1]) || settings.center_latitude || 48.7867);
  const zoom = Number(settings.zoom || 16);
  const categories = (settings.categories || []).filter((category: any) => category.name);

  useEffect(() => {
    let active = true;
    getPublicMapComments(block.id).then((items) => { if (active) setComments(items); })
      .catch(() => { if (active) setComments([]); });
    return () => { active = false; };
  }, [block.id]);

  useEffect(() => {
    if (!mapElement.current) return undefined;
    const categoryColors: { [name: string]: string } = {};
    (settings.categories || []).forEach((category: any) => { categoryColors[category.name] = category.color || "#1976d2"; });
    const markers = comments.filter((item) => item.geom && item.geom.coordinates && item.geom.coordinates.length >= 2).map((item) => {
      const marker = new Feature({ geometry: new Point(fromLonLat(item.geom.coordinates)) });
      const color = categoryColors[item.category] || "#1976d2";
      marker.set("title", item.title);
      marker.set("comment", item.comment);
      marker.setStyle(new Style({ image: new CircleStyle({ radius: 8, fill: new Fill({ color }), stroke: new Stroke({ color: "#fff", width: 2 }) }) }));
      return marker;
    });
    // Keep cached imagery for overview views and request full-resolution imagery for close-ups.
    const aerialSources = [new XYZ({ url: LGL_ORTHOPHOTO_TILES, maxZoom: 13,
      attributions: "© LGL Baden-Württemberg" }),
      new XYZ({ url: LGL_ORTHOPHOTO_TILES, minZoom: 16, maxZoom: 16,
        attributions: "© LGL Baden-Württemberg" }),
      new TileWMS({
        url: LGL_ORTHOPHOTO_WMS,
        params: { LAYERS: "IMAGES_DOP_20_RGB", FORMAT: "image/png", TRANSPARENT: true },
        attributions: "© LGL Baden-Württemberg",
      })];
    setImageryUnavailable(false);
    aerialSources.forEach((source) => {
      source.on("tileloaderror", () => setImageryUnavailable(true));
      source.on("tileloadend", () => setImageryUnavailable(false));
    });
    const map = new Map({ target: mapElement.current, layers: [
      new TileLayer({ source: new OSM() }),
      new TileLayer({ source: aerialSources[0], maxZoom: 13 }),
      new TileLayer({ source: aerialSources[1], minZoom: 13, maxZoom: 17 }),
      new TileLayer({ source: aerialSources[2], minZoom: 17 }),
      new VectorLayer({ source: new VectorSource({ features: markers }) }),
    ], view: liveView.current || new View({ center: fromLonLat([longitude, latitude]), zoom: Math.min(zoom, MAX_ORTHOPHOTO_ZOOM), maxZoom: MAX_ORTHOPHOTO_ZOOM }) });
    liveView.current = map.getView();
    map.getTargetElement().style.cursor = "grab";
    map.on("pointermove", (event) => {
      map.getTargetElement().style.cursor = addingPointRef.current ? "crosshair" : map.hasFeatureAtPixel(event.pixel) ? "pointer" : "grab";
    });
    const popup = document.createElement("div");
    popup.style.cssText = "background:#fff;padding:12px 14px;border-radius:8px;box-shadow:0 2px 10px rgba(0,0,0,.25);max-width:280px;font:14px Arial,sans-serif;";
    const overlay = new Overlay({ element: popup, positioning: OverlayPositioning.BOTTOM_CENTER, offset: [0, -12], stopEvent: false });
    map.addOverlay(overlay);
    map.on("singleclick", (event) => {
      const selected = map.forEachFeatureAtPixel(event.pixel, (feature) => feature);
      if (allowComments && (addingPointRef.current || !selected)) {
        overlay.setPosition(undefined); setPointError(""); setPointNotice("");
        setDraftPoint(toLonLat(event.coordinate) as [number, number]); return;
      }
      if (!selected) { overlay.setPosition(undefined); return; }
      while (popup.firstChild) popup.removeChild(popup.firstChild);
      const title = document.createElement("strong");
      title.textContent = String(selected.get("title") || "");
      popup.appendChild(title);
      const comment = String(selected.get("comment") || "");
      if (comment) { const text = document.createElement("div"); text.style.marginTop = "6px"; text.textContent = comment; popup.appendChild(text); }
      overlay.setPosition(event.coordinate);
    });
    const updateMapSize = () => map.updateSize();
    const Observer = (window as any).ResizeObserver;
    const resizeObserver = Observer ? new Observer(updateMapSize) : null;
    if (resizeObserver) resizeObserver.observe(mapElement.current);
    window.addEventListener("resize", updateMapSize);
    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", updateMapSize);
      map.setTarget(undefined);
    };
  }, [comments, latitude, longitude, settings.categories, zoom, allowComments]);

  const maximizeLabel = maximized ? "Karte verkleinern" : "Karte maximieren";
  return <><Box
    style={{ width: "100%", minWidth: 0, borderRadius: mobile ? 12 : 16, overflow: "hidden", border: "1px solid #dbe6df", background: "#fff", boxShadow: "0 8px 24px rgba(20, 84, 44, .1)",
      ...(maximized ? { position: "fixed", inset: 0, zIndex: 1200, border: 0, borderRadius: 0, display: "flex", flexDirection: "column", overflow: "auto" } : {}) }}
    onKeyDown={(event: React.KeyboardEvent) => {
      if (maximized && event.key === "Escape") setMaximized(false);
    }}
  >
    <Box style={{ position: "relative", ...(maximized ? { flex: "1 0 200px" } : {}) }}>
      <div ref={mapElement} style={{ width: "100%", height: maximized ? "100%" : mobile ? "min(60vh, 440px)" : "clamp(480px, 65vh, 760px)", minHeight: maximized ? 0 : mobile ? 300 : 480 }} aria-label="Karte mit veröffentlichten Beiträgen" />
      <IconButton
        aria-label={maximizeLabel} title={maximizeLabel} onClick={() => setMaximized(value => !value)}
        style={{ position: "absolute", top: 8, right: 8, padding: 8, background: "#fff", color: "#173d28", boxShadow: "0 1px 4px rgba(0, 0, 0, .3)" }}
      >
        {maximized ? <FullscreenExitIcon /> : <FullscreenIcon />}
      </IconButton>
    </Box>
    <Box style={{ padding: mobile ? 16 : 20, display: "flex", flexDirection: mobile ? "column" : "row", gap: 16, alignItems: mobile ? "stretch" : "center", justifyContent: "space-between", borderTop: "1px solid #e2ebe5" }}>
      {categories.length > 0 && <Box style={{ minWidth: 0 }}>
        <Typography style={{ fontSize: "0.75rem", fontWeight: 800, color: "#50705c", marginBottom: 8 }}>Kategorien</Typography>
        <Box style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px" }}>
          {categories.map((category: any) => <Box key={category.name} style={{ display: "flex", alignItems: "center", fontSize: "0.85rem", color: "#254233", minWidth: 0, overflowWrap: "anywhere" }}>
            <span style={{ flexShrink: 0, width: 10, height: 10, borderRadius: "50%", marginRight: 7, background: category.color || "#1976d2" }} />{category.name}
          </Box>)}
        </Box>
      </Box>}
      {allowComments ? <Box style={{ flexShrink: 0, width: mobile ? "100%" : "auto" }}>
        <Button fullWidth={mobile} variant="contained" style={{ minHeight: 44, padding: "10px 16px", borderRadius: 8, color: "#fff", background: "#173d28", textTransform: "none", fontWeight: 700, boxShadow: "none" }} onClick={() => {
          addingPointRef.current = !addingPointRef.current; setAddingPoint(addingPointRef.current);
        }}>{addingPoint ? "Abbrechen" : "+ Beitrag hinzufügen"}</Button>
        {addingPoint && <Typography role="status" style={{ fontSize: "0.8rem", marginTop: 8, color: "#254233" }}>Tippen Sie auf die gewünschte Stelle in der Karte.</Typography>}
      </Box> : <Typography style={{ fontSize: "0.85rem", color: "#50705c" }}>Punkt auswählen für Details</Typography>}
    </Box>
    {imageryUnavailable && <Typography role="status" style={{ padding: "0 16px 12px" }}>
      Die Luftbilder sind beim Anbieter gerade nicht verfügbar. Sie können die Straßenkarte weiterhin nutzen und Feedback hinzufügen.
    </Typography>}
  </Box>
  <Dialog open={!!pointNotice} onClose={() => setPointNotice("")} fullWidth maxWidth="sm" aria-labelledby={`feedback-confirmation-${block.id}`}>
    <DialogTitle id={`feedback-confirmation-${block.id}`}>Feedback gespeichert</DialogTitle>
    <DialogContent>
      <Typography role="status">{pointNotice}</Typography>
    </DialogContent>
    <DialogActions><Button color="primary" autoFocus onClick={() => setPointNotice("")}>Schließen</Button></DialogActions>
  </Dialog>
  <Dialog open={!!draftPoint} onClose={() => { if (!savingPoint) setDraftPoint(null); }} fullWidth maxWidth="sm">
    <DialogTitle>Beitrag hinzufügen</DialogTitle>
    <DialogContent>
      <Typography variant="body2" style={{ marginBottom: 12 }}>
        Geben Sie Ihr Feedback zu diesem Ort ein. Nach dem Senden wird es gespeichert und von einem Administrator geprüft. Es erscheint erst nach der Freigabe auf der Karte.
      </Typography>
      {categories.length > 0 && <Autocomplete
        ListboxProps={{ style: { maxHeight: "calc(100dvh - 96px)", overflowX: "hidden", overflowWrap: "anywhere" } }}
        fullWidth size="small" options={categories.map((category: any) => String(category.name))}
        value={pointCategory || null} onChange={(_, category) => setPointCategory(category || "")}
        disabled={savingPoint} autoHighlight openOnFocus noOptionsText="Keine Treffer" clearText="Zurücksetzen" openText="Kategorien anzeigen" closeText="Schließen"
        renderInput={params => <TextField {...params} variant="outlined" margin="normal" label="Kategorie" placeholder="Kategorie suchen …" />}
      />}
      <TextField autoFocus required fullWidth margin="normal" label="Titel" value={pointTitle} inputProps={{ maxLength: 255 }}
        disabled={savingPoint} onChange={event => setPointTitle(event.target.value)} />
      <TextField fullWidth multiline rows={4} margin="normal" label="Kommentar (optional)" value={pointComment}
        helperText="Zum Vergrößern oder Verkleinern die untere rechte Ecke ziehen."
        inputProps={{ style: { resize: "vertical", minHeight: 96, maxHeight: "50vh", overflowY: "auto" } }}
        disabled={savingPoint} onChange={event => setPointComment(event.target.value)} />
      {pointError && <Typography color="error" role="alert">{pointError}</Typography>}
    </DialogContent>
    <DialogActions>
      <Button disabled={savingPoint} onClick={() => setDraftPoint(null)}>Abbrechen</Button>
      <Button variant="contained" color="primary" disabled={savingPoint || !pointTitle.trim()} onClick={savePoint}>
        {savingPoint ? "Sendet…" : "Beitrag senden"}
      </Button>
    </DialogActions>
  </Dialog></>;
}

const isVisibleCard = (card: any) => {
  if (card.is_hidden === true) return false;
  return !card.hidden_cart_at || moment(card.hidden_cart_at).endOf("day").isSameOrAfter(moment(), "day");
};

const sortByNewestStart = (cards: any[]) => [...cards].sort((a, b) =>
  moment(b.startDateTime || b.dateLabel || 0).valueOf() - moment(a.startDateTime || a.dateLabel || 0).valueOf()
);

const formatEventSchedule = (card: any) => {
  if (!card.startDateTime) {
    return `${card.dateLabel ? moment(card.dateLabel).format("DD.MM.YYYY") : ""}${card.startTime ? ` ${card.startTime}${card.endTime ? ` – ${card.endTime}` : ""} Uhr` : ""}`.trim();
  }
  const start = moment(card.startDateTime);
  const end = card.endDateTime ? moment(card.endDateTime) : null;
  if (!end) return start.format("DD.MM.YYYY · HH:mm [Uhr]");
  return start.isSame(end, "day")
    ? `${start.format("DD.MM.YYYY · HH:mm")} – ${end.format("HH:mm")} Uhr`
    : `${start.format("DD.MM.YYYY, HH:mm")} – ${end.format("DD.MM.YYYY, HH:mm")} Uhr`;
};

function ContactForm({ block, primaryColor, siteId }: { block: Block; primaryColor: string; siteId?: number }) {
  const [values, setValues] = useState({ first_name: "", last_name: "", email: "", company_name: "", address: "", title: "", message: "", rules_accepted: false });
  const [status, setStatus] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const setValue = (key: string, value: string | boolean) => setValues(current => ({ ...current, [key]: value }));
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!values.rules_accepted) { setStatus("Bitte bestätigen Sie die Regeln und Bedingungen."); return; }
    if (!siteId) { setStatus("Dieses Kontaktformular ist nicht verfügbar."); return; }
    setSending(true); setStatus(null);
    try { const result = await submitContactForm({ site: siteId, ...values }); const referenceId = result.referenceId || result.reference_id; setStatus(`Vielen Dank. Ihre Nachricht wurde übermittelt. Ihre Referenznummer: ${referenceId || "wird erstellt"}. Bitte geben Sie diese bei Rückfragen an.`); setValues({ first_name: "", last_name: "", email: "", company_name: "", address: "", title: "", message: "", rules_accepted: false }); }
    catch { setStatus("Ihre Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es später erneut."); }
    finally { setSending(false); }
  };
  return <Box component="form" onSubmit={submit} style={{ margin: "32px 0", padding: 24, border: "1px solid #dbe6df", borderRadius: 12 }}>
    {block.title && <Typography variant="h4" style={{ fontWeight: 700, marginBottom: 20, color: primaryColor }}>{block.title}</Typography>}
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}><TextField required fullWidth label="Vorname" value={values.first_name} onChange={e => setValue("first_name", e.target.value)} /></Grid>
      <Grid item xs={12} sm={6}><TextField required fullWidth label="Nachname" value={values.last_name} onChange={e => setValue("last_name", e.target.value)} /></Grid>
      <Grid item xs={12}><TextField required fullWidth type="email" label="E-Mail" value={values.email} onChange={e => setValue("email", e.target.value)} /></Grid>
      <Grid item xs={12}><TextField fullWidth label="Unternehmen" value={values.company_name} onChange={e => setValue("company_name", e.target.value)} /></Grid>
      <Grid item xs={12}><TextField fullWidth label="Adresse" multiline rows={2} value={values.address} onChange={e => setValue("address", e.target.value)} /></Grid>
      <Grid item xs={12}><TextField required fullWidth label="Betreff" value={values.title} onChange={e => setValue("title", e.target.value)} /></Grid>
      <Grid item xs={12}><TextField required fullWidth label="Nachricht" multiline rows={5} value={values.message} onChange={e => setValue("message", e.target.value)} /></Grid>
      <Grid item xs={12}><FormControlLabel control={<Checkbox checked={values.rules_accepted} onChange={e => setValue("rules_accepted", e.target.checked)} color="primary" />} label="Ich bestätige die Regeln und Bedingungen." /></Grid>
      <Grid item xs={12}><Button type="submit" variant="contained" disabled={sending || isAuthenticatedPreview} style={{ backgroundColor: primaryColor, color: "#fff" }}>{sending ? "Wird gesendet…" : "Nachricht senden"}</Button>{status && <Typography style={{ marginTop: 12 }} color={status.startsWith("Vielen") ? "primary" : "error"}>{status}</Typography>}</Grid>
    </Grid>
  </Box>;
}

export default function CmsBlockRenderer({
  block,
  teamMembers = [],
  primaryColor = "#00b5e2",
  allPages = [],
  basePath = "",
  projectLeader = null,
  siteId,
}: CmsBlockRendererProps) {
  const [selectedEventCardId, setSelectedEventCardId] = useState<string | number | null>(null);
  const [pdfPreview, setPdfPreview] = useState<{ url: string; title: string } | null>(null);
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Page cards can either point to another page in this CMS or to a manually
  // entered URL from the CMS admin. React Router only understands local paths;
  // hand absolute URLs to the browser instead.
  const openCardLink = (link: string) => {
    if (!link || link === "#") return;

    if (/^(?:(?:https?:)?\/\/|mailto:|tel:)/i.test(link)) {
      window.location.assign(link);
      return;
    }

    history.push(link, { source: "cms" });
  };

  const useStyles = makeStyles((t) => ({
    faqAnswerContent: {
      lineHeight: 1.7,
      color: "#4b5863",
      fontSize: "1rem",
      fontWeight: 400,
      maxWidth: "85ch",
      "& p": { margin: "0 0 12px" },
      "& *:last-child": { marginBottom: 0 },
      "& ul, & ol": { margin: "0 0 12px", paddingLeft: 22 },
      "& h1, & h2, & h3, & h4, & h5, & h6": { color: primaryColor },
    },
    accordionDetails: {
      padding: "0 20px 20px",
      backgroundColor: "#fff",
      "& a": { color: primaryColor, textDecoration: "underline", fontWeight: 400 },
    },
    faqAccordion: {
      "&::before": { display: "none" },
      "&.Mui-expanded": { margin: "0 0 10px" },
      "& .MuiAccordionSummary-root": { minHeight: 56, padding: "0 20px", backgroundColor: "#fff" },
      "& .MuiAccordionSummary-content": { margin: "16px 0" },
      "& .MuiAccordionSummary-root.Mui-expanded": { minHeight: 56 },
      "& .MuiAccordionSummary-content.Mui-expanded": { margin: "16px 0" },
      "& .MuiAccordionSummary-root:hover": { backgroundColor: "#f8fafc" },
      "& .MuiAccordionSummary-root.Mui-focusVisible": { outline: `2px solid ${primaryColor}`, outlineOffset: -2 },
    },
    htmlContent: {
      minWidth: 0,
      overflowWrap: "anywhere",
      "& img, & video, & iframe": { maxWidth: "100%" },
      [t.breakpoints.down("sm")]: {
        fontSize: "1rem",
        lineHeight: 1.65,
        fontWeight: 400,
        marginBottom: 20,
        "& p": { marginTop: 0, marginBottom: 16 },
        "& ul, & ol": { paddingLeft: 22, marginTop: 12, marginBottom: 20 },
        "& li": { paddingLeft: 2, marginBottom: 10 },
        "& h1, & h2, & h3, & h4, & h5, & h6": {
          lineHeight: 1.25, overflowWrap: "anywhere", hyphens: "auto", marginTop: 24, marginBottom: 14,
        },
        "& h1": { fontSize: "1.75rem" },
        "& h2": { fontSize: "1.5rem" },
        "& h3": { fontSize: "1.25rem" },
        "& h4, & h5, & h6": { fontSize: "1.1rem" },
        "& table": { display: "block", maxWidth: "100%", overflowX: "auto" },
      },
      marginBottom: 32,
      lineHeight: 1.8,
      fontSize: "1.1rem",
      color: "#444",
      fontWeight: 500,

      "& a[href^='mailto:'], & .cms-email-link": {
        color: primaryColor,
        textDecoration: "underline",
        textUnderlineOffset: 3,
        fontWeight: 700,
        backgroundColor: "transparent",
        borderBottom: "none",
        padding: 0,
      },
      "& a[href^='mailto:']::before, & .cms-email-link::before": {
        content: '"✉ "',
        color: primaryColor,
      },
      "& a[href^='mailto:']::after, & .cms-email-link::after": {
        content: '""',
      },
      "& a[href^='tel:']": {
        color: "inherit",
        textDecoration: "none",
        borderBottom: `2px dotted ${primaryColor}`,
        fontWeight: 800,
      },
      "& a[href^='tel:']::before": {
        content: '"📞 "',
        color: primaryColor,
      },
      "& a[href^='tel:']:hover": {
        color: primaryColor,
        borderBottomStyle: "solid",
      },

      "& a:not([href^='mailto:']):not([href^='tel:'])": {
        color: primaryColor,
        backgroundColor: `${primaryColor}12`,
        borderBottom: `4px solid ${primaryColor}`,
        textDecoration: "none",
        padding: "1px 7px",
        borderRadius: 4,
        fontWeight: 900,
        fontSize: "1.08em",
        letterSpacing: "0.01em",
        transition: "all 0.2s ease",
      },

      "& a:not([href^='mailto:']):not([href^='tel:'])::after": {
        content: '" ↗"',
        fontSize: "1.05em",
        fontWeight: 900,
      },

      "& a:not([href^='mailto:']):not([href^='tel:']):hover": {
        backgroundColor: `${primaryColor}22`,
        color: primaryColor,
      },
      "& h1, & h2, & h3, & h4, & h5, & h6": {
        color: primaryColor,
      },
    },
  }));

  const classes = useStyles();

  const getAssetUrl = (url?: string) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    if (RootURL.startsWith("/")) return url.startsWith("/") ? url : `/${url}`;
    // RootURL is the CMS API path (`.../cms/`), while media is served from
    // the backend root (`.../media/`).
    const baseUrl = RootURL.replace(/\/cms\/?$/, "");
    const path = url.startsWith("/") ? url : `/${url}`;
    return `${baseUrl}${path}`;
  };

  const getImageUrl = (url?: string, width = 960) => {
    const assetUrl = getAssetUrl(url);
    if (!assetUrl || !/\.(jpe?g|png|webp)(?:\?.*)?$/i.test(assetUrl)) {
      return assetUrl;
    }
    return `${assetUrl}${assetUrl.includes("?") ? "&" : "?"}w=${width}`;
  };

  const isPdfAsset = (url?: string) => {
    try {
      return /\.pdf$/i.test(decodeURIComponent(new URL(url || "", window.location.href).pathname));
    } catch {
      return false;
    }
  };

  const renderImageSource = (source?: string) =>
    source ? (
      <Typography
        variant="caption"
        display="block"
        color="textSecondary"
        style={{ marginTop: 4 }}
      >
        Quelle: {source}
      </Typography>
    ) : null;

  const getGridWidths = (layoutType: string): number[] => {
    switch (layoutType) {
      case "l_1":
        return [12];
      case "l_1_1":
        return [6, 6];
      case "l_1_1_1":
        return [4, 4, 4];
      case "l_1_1_1_1":
        return [3, 3, 3, 3];
      case "l_1_2":
        return [4, 8];
      case "l_2_1":
        return [8, 4];
      case "l_1_2_1":
        return [3, 6, 3];
      default:
        return [12];
    }
  };

  const cleanPublicHtml = (html = "") => {
    const emailRegex = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

    let cleaned = html
      .replace(/<a\b[^>]*>\s*<\/a>/gi, "")

      // Fix existing wrong email links completely
      .replace(
        /<a\b[^>]*href=["']https?:\/\/([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})["'][^>]*>.*?<\/a>/gi,
        '<a href="mailto:$1" class="cms-email-link">$1</a>'
      )

      // Fix existing mailto email links completely
      .replace(
        /<a\b[^>]*href=["']mailto:([^"']+)["'][^>]*>.*?<\/a>/gi,
        (_match, email) => {
          const cleanEmail = String(email).trim();
          return `<a href="mailto:${cleanEmail}" class="cms-email-link">${cleanEmail}</a>`;
        }
      );

    // Protect existing links before converting plain emails
    const links: string[] = [];
    cleaned = cleaned.replace(/<a\b[^>]*>.*?<\/a>/gi, (match) => {
      links.push(match);
      return `__CMS_LINK_${links.length - 1}__`;
    });

    cleaned = cleaned.replace(emailRegex, (email) => {
      return `<a href="mailto:${email}" class="cms-email-link">${email}</a>`;
    });

    cleaned = cleaned.replace(/__CMS_LINK_(\d+)__/g, (_match, index) => {
      return links[Number(index)];
    });

    return formatQuestionLists(cleaned);
  };

  const renderDocumentCard = (
    asset: any,
    documentTitle?: string,
    documentDescription?: string
  ) => {
    const fileName = asset.file?.split("/").pop() || "";
    const normalizeFileLabel = (value: unknown) =>
      decodeURIComponent(String(value || ""))
        .split("/")
        .pop()
        ?.replace(/\.[^.]+$/, "")
        .toLocaleLowerCase()
        .replace(/[\s_.-]+/g, "") || "";
    const alias = String(documentTitle || "").trim();
    // The original filename is often prefilled into the alias field. Only a
    // deliberately different alias belongs on the public page.
    const titleFromAlias =
      alias && normalizeFileLabel(alias) !== normalizeFileLabel(fileName)
        ? alias
        : "";
    const extension = fileName.includes(".")
      ? fileName.split(".").pop().toUpperCase()
      : "DATEI";
    const assetUrl = getAssetUrl(asset.file);

    return (
      <Card
        elevation={0}
        style={{
          border: `1px solid ${primaryColor}2e`,
          borderLeft: `5px solid ${primaryColor}`,
          borderRadius: 16,
          background: `linear-gradient(135deg, #fff 0%, ${primaryColor}08 100%)`,
          boxShadow: "0 10px 28px rgba(0,0,0,0.07)",
          overflow: "hidden",
        }}
      >
        <CardContent
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            flexWrap: "wrap",
            alignItems: isMobile ? "stretch" : "center",
            gap: isMobile ? 16 : 20,
            padding: isMobile ? 20 : "24px 28px",
          }}
        >
          <Box
            style={{
              flex: "0 0 auto",
              width: 56,
              height: 56,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 14,
              backgroundColor: `${primaryColor}14`,
              color: primaryColor,
            }}
          >
            <DescriptionOutlinedIcon fontSize="large" />
          </Box>

          <Box
            style={{
              // In a column layout, a 280px flex basis becomes a forced
              // vertical height. Keep the text content-sized on mobile.
              flex: isMobile ? "0 1 auto" : "1 1 280px",
              minWidth: 0,
            }}
          >
            {titleFromAlias && (
              <Typography
                variant="h6"
                style={{ fontWeight: 800, lineHeight: 1.3, color: "#2f2f2f" }}
              >
                {titleFromAlias}
              </Typography>
            )}
            {documentDescription && (
              <Typography
                variant="body2"
                style={{ color: "#666", lineHeight: 1.6, marginTop: 6 }}
              >
                {documentDescription}
              </Typography>
            )}
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ display: "block", marginTop: 7, fontWeight: 700 }}
            >
              {extension}-Dokument
            </Typography>
          </Box>

          {isPdfAsset(assetUrl) && (
            <Button
              variant="outlined"
              onClick={() => setPdfPreview({ url: assetUrl, title: titleFromAlias || fileName || "PDF" })}
              style={{
                flex: "0 0 auto",
                alignSelf: isMobile ? "stretch" : "center",
                borderColor: primaryColor,
                color: primaryColor,
                borderRadius: 10,
                padding: "10px 18px",
                fontWeight: 800,
                textTransform: "none",
              }}
            >
              PDF ansehen
            </Button>
          )}
          <Button
            component="a"
            href={assetUrl}
            target="_blank"
            rel="noopener noreferrer"
            download
            variant="contained"
            startIcon={<GetAppIcon />}
            style={{
              flex: "0 0 auto",
              marginLeft: isMobile ? 0 : "auto",
              alignSelf: isMobile ? "stretch" : "center",
              backgroundColor: primaryColor,
              color: "#fff",
              borderRadius: 10,
              padding: "11px 18px",
              fontWeight: 800,
              textTransform: "none",
              boxShadow: `0 6px 16px ${primaryColor}3d`,
            }}
          >
            Herunterladen
          </Button>
        </CardContent>
      </Card>
    );
  };

  const renderHeading = (headingType: string, text: React.ReactNode) => {
    const headings: {
      [key: string]: { variant: any; component: any };
    } = {
      header_1: { variant: "h3", component: "h1" },
      header_2: { variant: "h4", component: "h2" },
      header_3: { variant: "h5", component: "h3" },
      header_4: { variant: "h6", component: "h4" },
      header_5: { variant: "subtitle1", component: "h5" },
    };
    const heading = headings[headingType] || headings.header_2;

    return (
      <Typography
        className="cms-content-heading"
        variant={heading.variant}
        component={heading.component}
        style={{
          marginTop: 8,
          marginBottom: 8,
          fontWeight: 800,
          lineHeight: 1.25,
          fontSize: isMobile ? ({ header_1: "1.75rem", header_2: "1.5rem", header_3: "1.25rem", header_4: "1.1rem", header_5: "1rem" } as { [key: string]: string })[headingType] : undefined,
          overflowWrap: "anywhere",
          hyphens: "auto",
          color: primaryColor,
          textAlign: "left",
        }}
      >
        {text}
      </Typography>
    );
  };

  const renderSlot = (slot: any) => {
    // Older/single-column CMS data can contain a slot without an explicit
    // type. In that case the slot represents the parent block's content.
    const slotType = String(slot.type || block.blockType || "")
      .trim()
      .toLowerCase();

    switch (slotType) {
      case "header_1":
      case "header_2":
      case "header_3":
      case "header_4":
      case "header_5":
        return renderHeading(slotType, slot.title || slot.content);
      case "html":
        return (
          <Box
            className={`${classes.htmlContent} cms-editorial-content`}
            dangerouslySetInnerHTML={{
              __html: cleanPublicHtml(slot.content || ""),
            }}
          />
        );
      case "image":
        const slotAssetId = slot.assetId || slot.asset_id;
        const assetForImage =
          block.assets?.find((a) => String(a.id) === String(slotAssetId)) ||
          slot.asset;
        const imageUrl = getImageUrl(
          slot.url || slot.file || slot.asset?.file || assetForImage?.file
        );
        if (!imageUrl) return null;
        const imageFileName = String(assetForImage?.file || "").split("/").pop() || "";
        const normalizeFileLabel = (value: unknown) =>
          String(value || "")
            .split(/[?#]/)[0]
            .split("/")
            .pop()
            ?.replace(/\.[^.]+$/, "")
            .toLocaleLowerCase()
            .replace(/[\s_.-]+/g, "") || "";
        const imageAlias = String(slot.title || "").trim();
        // The editor may prefill Alias / Titel from the uploaded filename.
        // A caption is shown only for a deliberately different alias.
        const imageTitle =
          imageAlias &&
          normalizeFileLabel(imageAlias) !== normalizeFileLabel(imageFileName)
            ? imageAlias
            : "";
        return (
          <Box style={{ margin: "24px 0", textAlign: "center" }}>
            <img
              src={imageUrl}
              alt={imageTitle}
              loading="lazy"
              decoding="async"
              style={{
                maxWidth: "100%",
                height: "auto",
                borderRadius: 16,
                boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
              }}
            />
            {imageTitle && (
              <Typography
                variant="caption"
                display="block"
                color="textSecondary"
                style={{ marginTop: 16, fontStyle: "italic" }}
              >
                {imageTitle}
              </Typography>
            )}
            {renderImageSource(assetForImage?.source)}
          </Box>
        );
      case "upload":
        const asset = block.assets?.find(
          (a) => a.id === slot.assetId || a.id === slot.asset_id
        );
        if (!asset) return null;
        const isSingleDocument = block.settings?.slots?.length === 1;
        const documentTitle =
          slot.title ||
          (isSingleDocument && block.title) ||
          undefined;
        const documentDescription =
          asset.description || slot.description || slot.asset?.description;
        return (
          <Box style={{ margin: "16px 0" }}>
            {renderDocumentCard(asset, documentTitle, documentDescription)}
          </Box>
        );
      case "gallery":
      case "page_cards":
      case "person_list":
      case "contact_form":
      case "faq":
        // For complex types, reuse the existing logic by creating a dummy block
        const dummyBlock = {
          ...block,
          blockType: slotType,
          settings: slot.settings || block.settings,
        };
        return (
          <CmsBlockRenderer
            block={dummyBlock}
            teamMembers={teamMembers}
            primaryColor={primaryColor}
            allPages={allPages}
            basePath={basePath}
            siteId={siteId}
          />
        );
      default:
        return null;
    }
  };

  const renderContent = () => {
    if (block.settings?.slots && block.settings.slots.length > 0) {
      const widths = getGridWidths(block.layoutType);
      return (
        <Grid container spacing={4}>
          {block.settings.slots.map((slot: any, idx: number) => (
            <Grid item xs={12} md={(widths[idx] || 12) as any} key={idx}>
              {renderSlot(slot)}
            </Grid>
          ))}
        </Grid>
      );
    }
    // Older CMS records can contain a differently cased or padded block type.
    // Normalize it before rendering so heading variants remain compatible.
    const blockType = String(block.blockType || "")
      .trim()
      .toLowerCase();

    switch (blockType) {
      case "header_1":
      case "header_2":
      case "header_3":
      case "header_4":
      case "header_5":
        return renderHeading(blockType, block.title || block.content);

      case "html":
        return (
          <Box
            className={`${classes.htmlContent} cms-editorial-content`}
            dangerouslySetInnerHTML={{
              __html: cleanPublicHtml(block.content || ""),
            }}
          />
        );

      case "image":
        return (
          <Box style={{ margin: "24px 0", textAlign: "center" }}>
            {block.assets?.[0]?.file && (
              <img
                src={getImageUrl(block.assets[0].file)}
                alt={
                  block.assets[0].title ||
                  block.assets[0].alt_text ||
                  block.title ||
                  ""
                }
                loading="lazy"
                decoding="async"
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  borderRadius: 16,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
                }}
              />
            )}
            {(block.assets?.[0]?.title || block.assets?.[0]?.caption) && (
              <Typography
                variant="caption"
                display="block"
                color="textSecondary"
                style={{ marginTop: 16, fontStyle: "italic" }}
              >
                {block.assets[0].title || block.assets[0].caption}
              </Typography>
            )}
            {renderImageSource(block.assets?.[0]?.source)}
          </Box>
        );

      case "image_text":
        return (
          <Grid
            container
            spacing={6}
            style={{ margin: "24px 0" }}
            alignItems="center"
          >
            <Grid item xs={12} md={6}>
              {block.assets?.[0]?.file && (
                <>
                  <img
                    src={getImageUrl(block.assets[0].file)}
                    alt={
                      block.assets[0].title ||
                      block.assets[0].alt_text ||
                      block.title ||
                      ""
                    }
                    loading="lazy"
                    decoding="async"
                    style={{
                      maxWidth: "100%",
                      height: "auto",
                      borderRadius: 16,
                      boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
                    }}
                  />
                  {renderImageSource(block.assets[0].source)}
                </>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {block.title && (
                <Typography
                  variant="h4"
                  gutterBottom
                  style={{
                    fontWeight: 700,
                    marginBottom: 24,
                    color: primaryColor,
                  }}
                >
                  {block.title}
                </Typography>
              )}
              <Box
                dangerouslySetInnerHTML={{
                  __html: cleanPublicHtml(block.content || ""),
                }}
                style={{ lineHeight: 1.8, color: "#444" }}
              />
            </Grid>
          </Grid>
        );

      case "faq":
        const faqItems =
          block.items ||
          block.settings?.faq_items ||
          block.settings?.faqItems ||
          [];
        return (
          <Box style={{ margin: "24px 0" }}>
            {block.title && (
              <Typography
                variant="h4"
                gutterBottom
                style={{
                  fontWeight: 700,
                  marginBottom: 20,
                  color: primaryColor,
                }}
              >
                {block.title}
              </Typography>
            )}
            {faqItems.map((item: any, idx: number) => (
              <Accordion
                className={classes.faqAccordion}
                key={item.id || idx}
                elevation={0}
                style={{
                  marginBottom: 10,
                  borderRadius: 6,
                  border: "1px solid #dfe5ea",
                  overflow: "hidden",
                  boxShadow: "none",
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon style={{ color: primaryColor }} />
                  }
                  
                >
                  <Typography style={{ fontWeight: 400, fontSize: "1rem", color: "#26343f" }}>
                    {item.question || item.title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionDetails}>
                  <Box style={{ width: "100%" }}>
                    <Box
                      className={classes.faqAnswerContent}
                      dangerouslySetInnerHTML={{
                        __html: cleanPublicHtml(
                          item.answer || item.content || ""
                        ),
                      }}
                    />
                    {item.answers &&
                      item.answers.map((ans: string, aIdx: number) => (
                        <Box
                          key={aIdx}
                          className={classes.faqAnswerContent}
                          style={{
                            marginTop: theme.spacing(2), // Reduced from 5
                          }}
                          dangerouslySetInnerHTML={{
                            __html: cleanPublicHtml(ans),
                          }}
                        />
                      ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        );

      case "person_list":
        const projectLeadMembers = projectLeader
          ? teamMembers.filter((member) => member.employeeId === projectLeader)
          : [];
        const otherTeamMembers = projectLeadMembers.length
          ? teamMembers.filter((member) => member.employeeId !== projectLeader)
          : teamMembers;
        const displayedTeamMembers = [...projectLeadMembers, ...otherTeamMembers];
        return (
          <Box className="cms-team-section" style={{ margin: "16px 0 32px", width: "100%" }}>
            <Typography component="h2" style={{ fontSize: 36, marginBottom: 8, color: "#24313b" }}>
              {block.title || "Ihre Ansprechpersonen"}
            </Typography>
            <Box style={{ width: 36, height: 3, background: primaryColor, marginTop: 14, marginBottom: 18, borderRadius: 2 }} />
            <Typography style={{ color: "#66717b", fontSize: 15, lineHeight: 1.6, marginBottom: 28 }}>
              Wir begleiten das Projekt und beantworten Ihre Fragen.
            </Typography>
            <Grid container spacing={3}>
              {displayedTeamMembers.map((member) => (
                <Grid item xs={12} sm={6} key={member.id} style={{ display: "flex" }}>
                  <Card className="cms-team-card" elevation={0} style={{
                    flex: 1, display: "flex", alignItems: "center", gap: 24,
                    padding: 28, borderRadius: 16, border: "1px solid #e1e5e9",
                    borderLeft: `4px solid ${primaryColor}`,
                    background: "#fff", minWidth: 0,
                    boxShadow: "0 6px 24px rgba(31,41,55,.04)",
                    transition: "transform 200ms ease, box-shadow 200ms ease",
                  }}>
                    <Avatar src={getImageUrl(member.image, 160)} style={{
                      width: 104, height: 136, borderRadius: 12, flexShrink: 0,
                      border: "1px solid #edf0f2",
                    }}>
                      {member.name?.charAt(0) || member.full_name?.charAt(0)}
                    </Avatar>
                    <CardContent style={{ padding: 0, minWidth: 0, flex: 1 }}>
                      <Typography component="h3" style={{ fontSize: 28, lineHeight: 1.2, color: "#24313b", marginBottom: 8 }}>
                        {member.name || member.full_name}
                      </Typography>
                      <Typography style={{ fontSize: 14, color: "#66717b", lineHeight: 1.5 }}>
                        {member.position}
                      </Typography>
                      {member.organisation && <Typography style={{ fontSize: 12, color: "#86909a", marginTop: 4 }}>{member.organisation}</Typography>}
                      {(member.email || member.phone) && <Box style={{ marginTop: 18 }}>
                        {member.email && <Typography component="div" style={{ fontSize: 14, marginBottom: 10 }}>
                          <span className="cms-team-email-control">
                            <a href={`mailto:${member.email}`} aria-label={`E-Mail an ${member.name || member.full_name} schreiben`} className="cms-team-email" style={{ display: "inline-flex", alignItems: "center", padding: "10px 12px", color: primaryColor, fontWeight: 400, textDecoration: "none", overflowWrap: "anywhere", userSelect: "text", minWidth: 0 }}>{member.email}</a>
                            <CopyTextButton value={member.email} label="E-Mail-Adresse" attached />
                          </span>
                        </Typography>}
                        {member.phone && <Typography style={{ fontSize: 14, color: "#53616b" }}>
                          <PhoneCallButton number={member.phone} email={member.email} />
                        </Typography>}
                      </Box>}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        );

      case "contact_form":
        return <ContactForm block={block} primaryColor={primaryColor} siteId={siteId} />;

      case "upload":
        return (
          <Box style={{ margin: "32px 0" }}>
            <Grid container spacing={3}>
              {block.assets?.map((asset) => (
                <Grid item xs={12} sm={6} key={asset.id}>
                  {renderDocumentCard(
                    asset,
                    block.assets?.length === 1 && block.title
                      ? block.title
                      : undefined,
                    asset.description
                  )}
                </Grid>
              ))}
            </Grid>
          </Box>
        );

      case "page_cards":
        const configuredCardDesign = block.settings?.cardDesign || "event";
        // Retain existing pages that briefly used the removed flip-card option.
        const cardDesign = configuredCardDesign === "timeline_flip" ? "timeline" : configuredCardDesign;
        const localCards = block.settings?.cards || [];
        const cards = cardDesign === "latest_news"
          ? allPages.flatMap((page: any) => (page.blocks || []).flatMap((pageBlock: any) =>
              pageBlock.blockType === "page_cards" && pageBlock.settings?.cardDesign !== "latest_news"
                ? (pageBlock.settings?.cards || []).filter((card: any) => card.is_published === true)
                : []))
          : localCards;
        const visibleCards = sortByNewestStart(cards.filter(isVisibleCard));
        // Only show the block if there are cards that are not empty
        const hasVisibleCards = visibleCards.some((card: any) => {
          const linkedPage = allPages.find(
            (p) => String(p.id) === String(card.pageId)
          );
          const isPagePublished =
            linkedPage &&
            linkedPage.is_published !== false &&
            linkedPage.isPublished !== false;

          return (
            card.title ||
            card.description ||
            card.image ||
            (card.pageId && isPagePublished)
          );
        });

        if (!hasVisibleCards) return null;

        if (cardDesign === "timeline" || cardDesign === "calendar") {
          const timelineCards = visibleCards
            .map((card: any, idx: number) => {
              const linkedPage = allPages.find(
                (p) =>
                  String(p.id) === String(card.pageId) &&
                  p.is_published !== false &&
                  p.isPublished !== false
              );
              const displayTitle = card.title || "";
              const displayDescription = card.description || "";
              const displayImage = card.image || linkedPage?.headerImage;
              const linkedPageSlug =
                linkedPage?.urlKey || linkedPage?.url_key || linkedPage?.slug;
              const linkedPageHasContent = Boolean(
                linkedPage?.blocks?.some(
                  (pageBlock: any) =>
                    pageBlock.content ||
                    pageBlock.title ||
                    pageBlock.assets?.length ||
                    pageBlock.settings?.cards?.length ||
                    pageBlock.settings?.faq_items?.length ||
                    pageBlock.settings?.slots?.some(
                      (slot: any) =>
                        slot.content ||
                        slot.asset_id ||
                        slot.assetId ||
                        slot.asset
                    )
                )
              );

              return {
                ...card,
                id: card.id || `${block.id}-${idx}`,
                displayTitle,
                displayDescription,
                imageUrl: displayImage ? getImageUrl(displayImage) : "",
                link:
                  card.url ||
                  (linkedPageSlug && linkedPageHasContent
                    ? `${basePath}/${linkedPageSlug}`
                    : "#"),
              };
            })
            .filter(
              (card: any) =>
                card.displayTitle ||
                card.displayDescription ||
                card.imageUrl ||
                card.dateLabel ||
                card.link !== "#"
            );

          if (cardDesign === "calendar") {
            return <EventCalendar cards={timelineCards} primaryColor={primaryColor} onOpen={(card) => openCardLink((card as any).link)} />;
          }
          return (
            <TimelineCards
              cards={timelineCards}
              title={block.title}
              primaryColor={primaryColor}
              onOpen={(card) => {
                openCardLink(card.link);
              }}
            />
          );
        }

        return (
          <Box style={{ margin: "32px 0" }}>
            {block.title && (
              <Typography
                variant="h4"
                gutterBottom
                style={{
                  fontWeight: 700,
                  marginBottom: 32,
                  color: primaryColor,
                }}
              >
                {block.title}
              </Typography>
            )}
            {cardDesign === "event" && (
              <EventCalendar
                cards={visibleCards.map((card: any, idx: number) => ({
                  ...card,
                  id: card.id || `${block.id}-${idx}`,
                  displayTitle: card.title || allPages.find((page) => String(page.id) === String(card.pageId))?.title || "Veranstaltung",
                }))}
                primaryColor={primaryColor}
                onOpen={(card) => {
                  const selectedId = String((card as any).id);
                  setSelectedEventCardId(selectedId);
                  window.setTimeout(() => document.getElementById(`event-card-${selectedId}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
                }}
              />
            )}
            <Grid container spacing={4}>
              {visibleCards.map((card: any, idx: number) => {
                const linkedPage = allPages.find(
                  (p) =>
                    String(p.id) === String(card.pageId) &&
                    p.is_published !== false &&
                    p.isPublished !== false
                );
                const displayTitle = card.title || linkedPage?.title || "";
                const displayDescription = card.description || "";
                const displayImage = card.image || linkedPage?.headerImage;
                const linkedPageSlug =
                  linkedPage?.urlKey || linkedPage?.url_key || linkedPage?.slug;
                const link = card.url || (linkedPageSlug
                  ? `${basePath}/${linkedPageSlug}`
                  : "#");

                if (!displayTitle && !displayDescription && !displayImage) {
                  return null;
                }

                return (
                  <Grid item xs={12} sm={6} md={4} key={idx} id={`event-card-${card.id || `${block.id}-${idx}`}`}>
                    <Card
                      elevation={0}
                      style={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 16,
                        border: String(card.id || `${block.id}-${idx}`) === String(selectedEventCardId) ? `4px solid ${primaryColor}` : "1px solid #eee",
                        boxShadow: String(card.id || `${block.id}-${idx}`) === String(selectedEventCardId) ? `0 0 0 5px ${primaryColor}24, 0 18px 36px ${primaryColor}38` : "none",
                        transform: String(card.id || `${block.id}-${idx}`) === String(selectedEventCardId) ? "scale(1.02)" : "scale(1)",
                        overflow: "hidden",
                        transition: "transform 0.3s, box-shadow 0.3s",
                        cursor: link !== "#" ? "pointer" : "default",
                      }}
                      onClick={() => {
                        openCardLink(link);
                      }}
                      onMouseEnter={(e) => {
                        if (link !== "#") {
                          e.currentTarget.style.transform = "translateY(-8px)";
                          e.currentTarget.style.boxShadow =
                            "0 12px 30px rgba(0,0,0,0.1)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      {displayImage && (
                        <Box
                          style={{
                            width: "100%",
                            paddingTop: "56.25%",
                            position: "relative",
                          }}
                        >
                          <img
                            src={getImageUrl(displayImage)}
                            alt={displayTitle}
                            loading="lazy"
                            decoding="async"
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        </Box>
                      )}
                      <CardContent style={{ padding: 24, flexGrow: 1 }}>
                        {(card.startDateTime || card.dateLabel || card.startTime) && (
                          <Box
                            display="flex"
                            alignItems="center"
                            style={{
                              gap: 4,
                              marginBottom: 8,
                              color: primaryColor,
                            }}
                          >
                            <CalendarTodayIcon style={{ fontSize: 16 }} />
                            <Typography
                              variant="caption"
                              style={{ fontWeight: "bold" }}
                            >
                              {formatEventSchedule(card)}
                            </Typography>
                          </Box>
                        )}
                        {card.location && (
                          <Box
                            display="flex"
                            alignItems="center"
                            style={{ gap: 4, marginBottom: 12, color: "#666" }}
                          >
                            <LocationOnIcon style={{ fontSize: 16 }} />
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 600 }}
                            >
                              {card.location}
                            </Typography>
                          </Box>
                        )}
                        {displayTitle && (
                          <Typography
                            variant="h6"
                            style={{
                              fontWeight: 700,
                              marginBottom: 12,
                              color: "#333",
                            }}
                          >
                            {displayTitle}
                          </Typography>
                        )}
                        {displayDescription && (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ lineHeight: 1.6 }}
                          >
                            {displayDescription}
                          </Typography>
                        )}
                      </CardContent>
                      <Box
                        style={{
                          padding: "10px 20px",
                          margin: "0 24px 24px",
                          color: "#fff",
                          backgroundColor: primaryColor,
                          fontWeight: 700,
                          borderRadius: 8,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          opacity: link !== "#" ? 1 : 0.7,
                          boxShadow:
                            link !== "#"
                              ? `0 4px 10px ${primaryColor}44`
                              : "none",
                          transition: "all 0.2s",
                          alignSelf: "flex-start",
                        }}
                      >
                        {link !== "#" ? "Mehr erfahren" : "In Vorbereitung"}{" "}
                        <span style={{ marginLeft: 8 }}>→</span>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        );

      case "gallery":
        return (
          <Box style={{ margin: "48px 0" }}>
            {block.title && (
              <Typography
                variant="h4"
                gutterBottom
                style={{
                  fontWeight: 800,
                  marginBottom: 32,
                  color: primaryColor,
                }}
              >
                {block.title}
              </Typography>
            )}

            <Grid container spacing={2}>
              {(Array.isArray(block.settings?.gallery_images)
                ? block.settings.gallery_images
                    .map((image: any) => image.asset || (block.assets || []).find(
                      (asset: any) => String(asset.id) === String(image.asset_id ?? image.assetId),
                    ))
                    .filter(Boolean)
                : block.assets || []).map((asset: any) => (
                <Grid item xs={12} sm={6} md={4} key={asset.id}>
                  <Box
                    borderRadius={12}
                    overflow="hidden"
                    bgcolor="#fff"
                    boxShadow="0 6px 18px rgba(0,0,0,.08)"
                  >
                    <img
                      src={getImageUrl(asset.file, 960)}
                      alt={asset.alt_text || asset.title || "Galeriebild"}
                      loading="lazy"
                      style={{
                        display: "block",
                        width: "100%",
                        height: 230,
                        objectFit: "cover",
                      }}
                    />
                    {(asset.title || asset.caption) && (
                      <Box p={2}>
                        <Typography variant="body2" style={{ fontWeight: 700 }}>
                          {asset.title || asset.caption}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        );

      case "map":
        return (
          <Box style={{ margin: "32px 0" }}>
            {block.title && (
              <Typography
                variant="h4"
                gutterBottom
                style={{
                  fontWeight: 700,
                  marginBottom: 24,
                  color: primaryColor,
                }}
              >
                {block.title}
              </Typography>
            )}
            <PublicOrthophotoMap block={block} />
          </Box>
        );

      default:
        return (
          <Typography color="error">
            Unbekannter Block-Typ: {block.blockType}
          </Typography>
        );
    }
  };

  return (
    <>
      <Grid item xs={12}>
        {renderContent()}
      </Grid>
      <Dialog
        open={Boolean(pdfPreview)}
        onClose={() => setPdfPreview(null)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="xl"
        PaperProps={{ style: { height: isMobile ? "100%" : "90vh", overflow: "hidden" } }}
        aria-labelledby="beteiligung-pdf-preview-title"
      >
        <DialogTitle id="beteiligung-pdf-preview-title" style={{ display: "flex", alignItems: "center", padding: "12px 16px" }}>
          <Typography style={{ flex: 1, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {pdfPreview?.title}
          </Typography>
          <IconButton aria-label="PDF-Vorschau schließen" onClick={() => setPdfPreview(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ display: "flex", minHeight: 0, padding: 0, backgroundColor: "#eef1f4" }}>
          {pdfPreview && (
            <iframe
              title={`PDF: ${pdfPreview.title}`}
              src={`${pdfPreview.url.split("#")[0]}#toolbar=1&navpanes=0&view=FitH`}
              style={{ width: "100%", height: "100%", border: 0 }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
